'use strict'

import Polyfills from './Polyfills.js'
import { Bartender } from '@fokke-/bartender.js'
import boringmenu from '@teppokoivula/boringmenu'
import Glightbox from 'glightbox'
import { createApp } from 'vue'
import VideoList from './vue-components/VideoList.vue'

/**
 * Site class contains general purpose site-specific features.
 *
 * @version 1.1.0
 */
export default class Site {
	/**
	 * Class constructor
	 *
	 * @param {Object} options Options for the class.
	 */
	constructor(options = {}) {
		this.options = {}

		// Init polyfills
		const polyfills = new Polyfills()
		polyfills.init()

		// Init off-canvas after mobile menu is ready
		document.addEventListener('boringmenu-init-done', () => {
			window.bartender = new Bartender({
				el: '.bartender-main',
				contentEl: '.bartender-content',
			}, {
				focusTrap: true,
			})

			window.bartender.addBar('mobileBar', {
				el: '.mobileBar',
				position: 'right',
				mode: 'float',
			})

			document.querySelector('.toggleMobileBar').addEventListener('click', (event) => {
				window.bartender.toggle('mobileBar', event.target)
			})

			document.querySelector('.closeMobileBar').addEventListener('click', (event) => {
				window.bartender.close()
			})

			window.bartender.addPushElement('.toggleMobileBar', {
				positions: [
					'right',
				],
			})
		})

		// Init mobile menu
		const mobileMenu = document.getElementById('mobile-menu')
		new boringmenu({
			selectors: {
				menu: '.menu-mobile__list--level-1',
			},
			classes: {
				item: 'menu-mobile__item',
				itemActive: 'menu-mobile__item--current',
				itemParent: 'menu-mobile__item--parent',
				toggle: 'menu-mobile__toggle',
				toggleTextContainer: 'sr-only',
			},
			labels: {
				'menu.open': mobileMenu
					? mobileMenu.getAttribute('data-labels-open')
					: 'open',
				'menu.close': mobileMenu
					? mobileMenu.getAttribute('data-labels-close')
					: 'close',
			},
			icons: {
				'menu.open': 'icon-open',
				'menu.close': 'icon-close',
			},
		})

		// Initialize
		this.init(options)
	}

	/**
	 * Init the class by calling applicable init methods
	 *
	 * @param {Object} options Options for the class.
	 * @return {Object}
	 */
	init(options = {}) {
		// Merge user options to the defaults
		this.options = {
			responsiveTables: {
				selector: 'main table',
			},
			imageLinks: {
				parentSelector: 'main',
			},
			...options,
		}

		// Call individual init methods
		this.initResponsiveTables()
		this.initSkipLinks()
		this.initImageLinks()
		this.initCardLinks()
		this.initVideoList()

		// Dispatch custom event when init is done
		document.dispatchEvent(
			new CustomEvent('site-init-done', {
				bubbles: true,
				cancelable: true,
			})
		)

		return this
	}

	/**
	 * Initialize responsive tables
	 *
	 * Finds content tables and wraps them with div.table-wrapper.
	 */
	initResponsiveTables() {
		document
			.querySelectorAll(this.options.responsiveTables.selector)
			.forEach((table) => {
				if (!table.closest('.table-wrapper')) {
					const tableWrapper = document.createElement('div')
					tableWrapper.classList.add('table-wrapper')
					tableWrapper.classList.add('overflow-x-auto')
					table.parentNode.insertBefore(tableWrapper, table)
					tableWrapper.appendChild(table)
				}
			})
	}

	/**
	 * Initialize skip links
	 *
	 * Finds skip links and enhances their behaviour for various screen readers and mobile devices.
	 */
	initSkipLinks() {
		const skipLinks = document.querySelectorAll(
			'.skip-link:not([data-skip-link])'
		)
		if (skipLinks.length) {
			const skipToBlur = (event) => {
				if (event.target.getAttribute('data-tabindex')) {
					event.target.removeAttribute('tabindex')
					event.target.removeAttribute('data-tabindex')
				}
			}
			skipLinks.forEach((skipLink) => {
				skipLink.setAttribute('data-skip-link', true)
				skipLink.addEventListener('click', (event) => {
					const skipTo = document.getElementById(
						event.target.href.split('#')[1]
					)
					if (skipTo && skipTo.getAttribute('tabindex') != '-1') {
						event.preventDefault()
						skipTo.setAttribute('tabindex', '-1')
						skipTo.setAttribute('data-tabindex', true)
						skipTo.addEventListener('blur', skipToBlur)
						skipTo.addEventListener('focusout', skipToBlur)
						skipTo.focus()
					}
				})
			})
		}
	}

	initCardLinks() {
		const cards = document.querySelectorAll('.js-card')
		if (!cards.length) return

		cards.forEach(card => {
			let down, up, link = card.querySelector('h3 a')

			card.onmousedown = () => down = +new Date()
			card.onmouseup = () => {
				up = +new Date()
				if ((up - down) < 200) {
					link.click()
				}
			}
		})
	}

	initVideoList () {
		const els = document.querySelectorAll('.video-list')
		if (!els.length) {
			return
		}

		for (const el of els) {
			createApp(VideoList, {
				categories: JSON.parse(el.dataset.categories || '[]'),
				videos: JSON.parse(el.dataset.videos || '[]'),
				color: JSON.parse(el.dataset.color) || '',
				translations: JSON.parse(el.dataset.translations || '{}'),
			}).mount(el)
		}
	}

	/**
	 * Initialize image links
	 */
	initImageLinks() {
		// Parent of image links
		let parentNode = document.querySelector(
			this.options.imageLinks.parentSelector
		)
		if (!parentNode) return

		// Add glightbox class to image links
		parentNode
			.querySelectorAll(
				'a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".gif"]'
			)
			.forEach((link) => {
				if (!link.classList.contains('glightbox')) {
					if (
						!link.getAttribute('data-title') &&
						!link.getAttribute('data-glightbox')
					) {
						let figcaption = link.parentNode.querySelector('figcaption')
						if (figcaption) {
							let caption = figcaption ? figcaption.textContent : ''
							link.setAttribute('data-title', caption)
						}
					}
					link.classList.add('glightbox')
				}
			})

		// Initialize GLightbox
		if (parentNode.querySelector('.glightbox')) {
			window.glightbox = Glightbox()
		}
	}
}
